import React, { useEffect } from 'react';
import { Group, Level, Box } from '@landr/maestro';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import VerboseError from 'error/components/VerboseError';
import SamplesTable from 'components/SamplesTable';
import { DefaultEmptyState } from 'components/EmptyState/DefaultState/DefaultState';
import Pagination from 'components/Pagination/Pagination';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import useGetCurrentBreakpoint from 'hooks/useGetCurrentBreakpoint/useGetCurrentBreakpoint';
import KeyboardCheatSheetInfo from 'components/KeyboardCheatSheetInfo/KeyboardCheatSheetInfo';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { usePrerenderComponentReady } from 'hooks/usePrerenderComponentReady/usePrerenderComponentReady';
import { getDetailsViewSizes } from '../helpers/getDetailsViewSizes';
import PaneResults from './PaneResults';
import { UpsellBanner } from './UpsellBanner';

const SamplesPane = ({
    customEmptyState: CustomEmptyState,
    showNoFilterCopy = false,
    dropdownSorting,
    onPageChanged,
}: {
    customEmptyState?: React.ComponentType;
    appended?: React.ComponentType;
    showNoFilterCopy?: boolean;
    dropdownSorting?: React.ReactNode;
    onPageChanged: () => void;
}) => {
    const currentBreakpoint = useGetCurrentBreakpoint();
    const { isSm, isMd, isMl, isLg } = getDetailsViewSizes({
        currentBreakpoint,
    });

    const {
        samplesPerPage,
        samples,
        samplesError,
        samplesLoading,
        samplesTotal,
    } = useSearchController();

    const { hasActiveFilters, page } = useCurrentSearchState();

    const { updateSamples } = useSelectedSamplesContext();

    useEffect(() => {
        if (!samples) {
            return;
        }

        updateSamples(samples);
        // Adding updateSamples create excessive re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [samples]);

    const showEmptyState = !samplesLoading && samplesTotal === 0;

    usePrerenderComponentReady({
        hasDoneRendering: !!samples,
        componentName: 'SamplesPane',
    });

    if (samplesError) {
        return <VerboseError />;
    }

    if (showEmptyState) {
        const showCustomEmptyState = !hasActiveFilters && CustomEmptyState;

        return showCustomEmptyState ? (
            <>
                <UpsellBanner />
                <CustomEmptyState />
            </>
        ) : (
            <DefaultEmptyState />
        );
    }

    return (
        <>
            <UpsellBanner />
            <Box pt="sm" minHeight="30px">
                <Level align="flex-start">
                    <Level.Item>
                        <Group gap="xs" alignItems="center">
                            <Group.Item>
                                <PaneResults
                                    isLoading={samplesLoading}
                                    total={samplesTotal}
                                    showNoFilterCopy={showNoFilterCopy}
                                />
                            </Group.Item>
                            <Group.Item>
                                <KeyboardCheatSheetInfo />
                            </Group.Item>
                        </Group>
                    </Level.Item>
                    <Level.Item>{dropdownSorting}</Level.Item>
                </Level>
            </Box>
            <SamplesTable
                key={`samples-pane-samples-table-page-${page}`}
                itemsPerPage={samplesPerPage}
                isFetching={samplesLoading}
                items={samples ?? []}
                isSm={isSm}
                isMd={isMd}
                isMl={isMl}
                isLg={isLg}
                onPageChanged={onPageChanged}
            />
            <Pagination
                itemsPerPage={samplesPerPage}
                itemCount={samplesTotal ?? 0}
                onPageChanged={onPageChanged}
            />
        </>
    );
};

SamplesPane.displayName = 'DetailsView.SamplesPane';

export default SamplesPane;
